import {
  BreakpointObserver,
  Breakpoints,
  BreakpointState,
} from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import {
  Component,
  ContentChild,
  inject,
  Input,
  OnInit,
  signal,
  TemplateRef,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { BehaviorSubject } from 'rxjs';

@Component({
  imports: [CommonModule, MatIconModule, MatButtonModule],
  selector: 'siat-carousel',
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.scss'],
})
export class CarouselComponent<T> implements OnInit {
  /**
   * The items to be displayed in the carousel.
   */
  @Input()
  items: T[] = [];

  /**
   * The color of the background.
   */
  @Input()
  backgroundColor: string;

  /**
   * The id of the carousel.
   */
  @Input()
  carouselId: number;

  /**
   * The template for the item.
   */
  @ContentChild(TemplateRef)
  itemTemplate!: TemplateRef<{ $implicit: T }>;

  /**
   * The template for the extra item.
   */
  @ContentChild('extraItem')
  extraTemplate!: TemplateRef<unknown>;

  /**
   * The current slide index.
   */
  currentSlide = 0;

  /**
   * The number of items that can be displayed in a single slide.
   */
  itemsPerSlide = signal(3);

  /**
   * The index of the expanded item.
   */
  private expandedItemIndex$ = new BehaviorSubject<number | null>(null);

  /**
   * Sets the index of the expanded item.
   */
  @Input()
  set expandedIndex(index: number | null) {
    this.expandedItemIndex$.next(index);
  }

  breakpointObserver = inject(BreakpointObserver);

  /**
   * @inheritdoc
   */
  ngOnInit(): void {
    this.observeBreakpoints();
    this.expandedItemIndex$.subscribe(index => {
      if (index != null) {
        this.goToSlideByItemIndex(index);
      }
    });
  }

  /**
   * Observes the breakpoints to determine the number of items per slide.
   */
  private observeBreakpoints(): void {
    this.breakpointObserver
      .observe([
        Breakpoints.XSmall,
        Breakpoints.Small,
        Breakpoints.Medium,
        Breakpoints.Large,
        Breakpoints.XLarge,
      ])
      .subscribe((state: BreakpointState) => {
        if (state.breakpoints[Breakpoints.XSmall]) {
          this.itemsPerSlide.set(2); // 1 item + 1 extra
        } else if (state.breakpoints[Breakpoints.Small]) {
          this.itemsPerSlide.set(3);
        } else if (state.breakpoints[Breakpoints.Medium]) {
          this.itemsPerSlide.set(4);
        } else if (state.breakpoints[Breakpoints.Large]) {
          this.itemsPerSlide.set(5);
        } else {
          this.itemsPerSlide.set(6);
        }
      });
  }

  /**
   * Returns the number of items that can be displayed in a single slide.
   */
  get effectiveItemsPerSlide(): number {
    return this.itemsPerSlide() - 1;
  }

  /**
   * Returns the total number of slides in the carousel.
   */
  get totalSlides(): number {
    return Math.ceil(this.items.length / this.effectiveItemsPerSlide);
  }

  /**
   * Returns the items that are currently visible in the carousel.
   */
  get visibleItems(): T[] {
    const start = this.currentSlide * this.effectiveItemsPerSlide;
    return this.items.slice(start, start + this.effectiveItemsPerSlide);
  }

  /**
   * Navigates to the next slide.
   */
  next(): void {
    if (this.currentSlide < this.totalSlides - 1) {
      this.currentSlide++;
    }
  }

  /**
   * Navigates to the previous slide.
   */
  prev(): void {
    if (this.currentSlide > 0) {
      this.currentSlide--;
    }
  }

  /**
   * A private method to navigate to a specific slide based on the item index.
   */
  private goToSlideByItemIndex(index: number): void {
    if (index == null || index < 0 || this.items.length === 0) {
      this.currentSlide = 0;
      return;
    }

    const clampedIndex = Math.min(index, this.items.length - 1);
    this.currentSlide = Math.floor(clampedIndex / this.effectiveItemsPerSlide);
  }

  /**
   * Returns the number of items not yet visible in subsequent slides.
   */
  get remainingItemsCount(): number {
    const shown = (this.currentSlide + 1) * this.effectiveItemsPerSlide;
    return Math.max(0, this.items.length - shown);
  }
}
